import React, { useState, useRef } from 'react'
import {
  Grid,
  makeStyles,
  FormSection,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormSectionTitle,
  useClientOptions
} from '@paintscout/ui'
import ConvertIcon from '@material-ui/icons/SwapVert'
import type { QuoteItemSection, RenderableItem, UpdateableLineItem } from '@paintscout/util/builder'
import {
  getConversionProducts,
  moveItems,
  updateLineItem,
  getFiles,
  convertToLineItem,
  getItems
} from '@paintscout/util/builder'
import type { DialogProps } from '@paintscout/ui'
import type { QuoteDocument } from 'paintscout'
import ItemTable, { AmountColumn, AmountHeadColumn, ItemColumn, ItemTableCell } from '../../ItemTable'
import { LineItemForm } from '../../forms'
import FilesGrid from '../../FilesGrid'

export interface ConvertItemsDialogProps extends DialogProps {
  quote: QuoteDocument
  itemKeys: string[]
  view?: 'area' | 'substrate'
  section?: QuoteItemSection
  onConfirm?: (updatedQuote: QuoteDocument) => void
  onCancel?: () => void
}

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.action.active
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '100%',
    paddingLeft: theme.spacing(),
    '& h4': {
      marginBottom: theme.spacing() / 4
    }
  }
}))

function ConvertItemsDialog({
  quote,
  itemKeys,
  view = 'area',
  section = 'bid',
  onConfirm,
  onCancel,
  ...otherProps
}: ConvertItemsDialogProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const originalItems = getItems({
    quote,
    options,
    view,
    keys: itemKeys,
    section,
    consumer: 'customer',
    trimBySubstrateSection: section
  })

  const products = getConversionProducts({ quote, options, view, section, items: originalItems })
  const [convertedItem, setConvertedItem] = useState<UpdateableLineItem>({
    ...convertToLineItem({ quote, options, itemKeys, section, products })
  })
  const files = getFiles({ quote, keys: convertedItem.files })

  function handleDetailsChange(lineItem: UpdateableLineItem) {
    setConvertedItem({
      ...convertedItem,
      ...lineItem
    })
  }

  function handleConfirm() {
    const updatedLineItem = {
      ...convertedItem,
      label: nameRef.current?.value,
      description: descriptionRef.current?.getHTML()
    }
    console.log({ updatedLineItem })
    const updatedQuote = updateLineItem({
      quote,
      options,
      item: updatedLineItem,
      insertAfterKey: itemKeys[0]
    })

    if (onConfirm) {
      const areaIds = originalItems.reduce((acc: { [key: string]: string[] }, item: RenderableItem) => {
        return {
          ...acc,
          [item.key]: [...(acc[item.key] ?? []), ...(item?.additionalFields?.areaIds ?? [])]
        }
      }, {})
      onConfirm(
        moveItems({
          quote: updatedQuote,
          options,
          keys: itemKeys,
          areaIds,
          section: 'archived',
          previousSection: section
        })
      )
    }
  }

  const rightContent = (
    <div className={classes.rightContent}>
      <Typography variant="h4" value={convertedItem.totals.afterMaterials} format={'price'} showUnits={true} />
    </div>
  )

  const nameRef = useRef(null)
  const descriptionRef = useRef(null)
  const crewNoteRef = useRef(null)
  const clientNoteRef = useRef(null)

  return (
    <Dialog onClose={onCancel} maxWidth="lg" fullWidth {...otherProps}>
      <DialogTitle rightContent={rightContent}>Convert to Line Item</DialogTitle>
      <DialogContent>
        <Grid container direction="column" alignItems="stretch" spacing={3}>
          <Grid item xs={12}>
            <FormSectionTitle title="Original Items" />
            <ItemTable
              items={originalItems}
              showAddButton={false}
              draggable={false}
              checkboxes={false}
              renderHeadColumns={() => (
                <>
                  <ItemTableCell>Item</ItemTableCell>
                  <ItemTableCell align="right">
                    <AmountHeadColumn consumer="crew" />
                  </ItemTableCell>
                  <ItemTableCell align="right">
                    <AmountHeadColumn consumer="customer" />
                  </ItemTableCell>
                </>
              )}
              renderRowColumns={(item: RenderableItem) => {
                return (
                  <>
                    <ItemTableCell>
                      <ItemColumn item={item} consumer={'customer'} isEditable section={section ?? 'bid'} />
                    </ItemTableCell>
                    <ItemTableCell align="right">
                      <AmountColumn
                        item={{
                          ...item,
                          value: (item?.additionalFields?.painting ?? 0) + (item?.additionalFields?.prep ?? 0)
                        }}
                        consumer={'crew'}
                      />
                    </ItemTableCell>
                    <ItemTableCell align="right">
                      <AmountColumn item={item} consumer={'customer'} />
                    </ItemTableCell>
                  </>
                )
              }}
            />
          </Grid>
          <Grid item container justifyContent="center">
            <ConvertIcon fontSize="large" className={classes.icon} />
          </Grid>
          <Grid item>
            <FormSectionTitle title="Converted Line Item" />
            <LineItemForm
              quote={quote}
              item={convertedItem}
              onChange={handleDetailsChange}
              showPricing={true}
              showMaterials
              autoFocus={false}
              nameRef={nameRef}
              descriptionRef={descriptionRef}
              clientNoteRef={clientNoteRef}
              crewNoteRef={crewNoteRef}
            />
          </Grid>
          {files.length > 0 && (
            <Grid item>
              <FormSection>
                <Typography variant="h4" gutterBottom>
                  Media
                </Typography>
                <FilesGrid files={files} editable={false} showUploadDropzone={false} />
              </FormSection>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button onClick={handleConfirm} variant={'contained'} disabled={!originalItems.length}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConvertItemsDialog
